// App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import Consulting from './pages/Consulting';
import TrainingCalendar from './pages/TrainingCalendar';
import TrainingCalendar2025 from './pages/TrainingCalendar2025';
import Training from './pages/Training';
import TrainingDetail from './pages/TrainingDetail';
import ConsultingDetail from './pages/ConsultingDetail';
import NotFound from './pages/NotFound';
import ExecutiveTraining from './pages/ExecutiveTraining';
import ExecutiveTrainingDetail from './pages/ExecutiveTrainingDetail';
import trainingData from './data/trainingData';
import consultingData from './data/consultingData';
import executiveTrainingData from './data/executiveTrainingData';
import './App.css';

// NEW: imports for transitions
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// Flatten executive training data for routing
const executiveTrainingRoutes = executiveTrainingData.reduce((acc, item) => {
  if (item.subItems) {
    return [
      ...acc,
      { path: item.path, description: item.description, title: item.title },
      ...item.subItems.map((subItem) => ({
        path: subItem.path,
        description: subItem.description,
        title: subItem.title,
      })),
    ];
  }
  return [...acc, { path: item.path, description: item.description, title: item.title }];
}, []);

// Small helper so only the route area animates
function RouteTransitions({ children }) {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait" initial={true}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}          // no lift
        animate={{ opacity: 1 }}          // fade in
        exit={{ opacity: 0 }}             // fade out
        transition={{
          duration: prefersReducedMotion ? 0 : 0.1, // a little quicker than before
          ease: 'easeOut',
        }}
        style={{ willChange: 'opacity', backfaceVisibility: 'hidden' }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          {children}
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <div className="app-container">
        <Navbar />
        <div className="content-wrap">
          <RouteTransitions>
            <Routes>
              {/* Static Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/consulting" element={<Consulting />} />
              <Route path="/training-calendar" element={<TrainingCalendar />} />
              <Route path="/training-calendar-2025" element={<TrainingCalendar2025 />} />

              {/* Training Overview Route */}
              <Route path="/training" element={<Training />} />

              {/* Dynamic Routes for Each Training Item */}
              {trainingData.map((training, index) => (
                <Route
                  key={index}
                  path={training.path}
                  element={<TrainingDetail training={training} trainingData={trainingData} />}
                />
              ))}

              {/* Dynamic Routes for Each Consulting Item */}
              {consultingData.map((consulting, index) => (
                <Route
                  key={index}
                  path={consulting.path}
                  element={<ConsultingDetail consulting={consulting} consultingData={consultingData} />}
                />
              ))}

              {/* Executive Training Overview Route */}
              <Route path="/executive-training" element={<ExecutiveTraining />} />

              {/* Dynamic Routes for Each Executive Training Item */}
              {executiveTrainingRoutes.map((training, index) => (
                <Route
                  key={`executive-${index}`}
                  path={training.path}
                  element={<ExecutiveTrainingDetail training={training} executiveTrainingData={executiveTrainingData} />}
                />
              ))}

              {/* 404 Not Found Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </RouteTransitions>
        </div>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
